import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../services/firebaseConfig';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

// Password validation rules
const PASSWORD_RULES = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true,
  requireSpecial: true,
};

// Sign-in attempt tracking
const SIGNIN_LIMIT = {
  maxAttempts: 5,
  timeWindow: 15 * 60 * 1000, // 15 minutes
};

const signInAttempts = new Map();

const validatePassword = (password) => {
  if (password.length < PASSWORD_RULES.minLength) {
    throw new Error(`Password must be at least ${PASSWORD_RULES.minLength} characters long`);
  }
  if (PASSWORD_RULES.requireUppercase && !/[A-Z]/.test(password)) {
    throw new Error('Password must contain at least one uppercase letter');
  }
  if (PASSWORD_RULES.requireLowercase && !/[a-z]/.test(password)) {
    throw new Error('Password must contain at least one lowercase letter');
  }
  if (PASSWORD_RULES.requireNumber && !/\d/.test(password)) {
    throw new Error('Password must contain at least one number');
  }
  if (PASSWORD_RULES.requireSpecial && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    throw new Error('Password must contain at least one special character');
  }
};

const checkSignInThrottle = (email) => {
  const now = Date.now();
  const userAttempts = signInAttempts.get(email) || { count: 0, timestamp: now };

  // Reset attempts if time window has passed
  if (now - userAttempts.timestamp > SIGNIN_LIMIT.timeWindow) {
    userAttempts.count = 0;
    userAttempts.timestamp = now;
  }

  if (userAttempts.count >= SIGNIN_LIMIT.maxAttempts) {
    const timeLeft = Math.ceil(
      (SIGNIN_LIMIT.timeWindow - (now - userAttempts.timestamp)) / 1000 / 60
    );
    throw new Error(`Too many login attempts. Please try again in ${timeLeft} minutes`);
  }

  userAttempts.count++;
  signInAttempts.set(email, userAttempts);
};

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authUnsubscribe, setAuthUnsubscribe] = useState(null);

  async function signup(email, password) {
    try {
      validatePassword(password);
    } catch (error) {
      throw new Error(error.message);
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Create user document in Firestore
      const userData = {
        email: user.email,
        isAdmin: false,
        createdAt: new Date().toISOString(),
        uid: user.uid,
        lastPasswordChange: new Date().toISOString(),
      };

      await setDoc(doc(db, 'users', user.uid), userData);

      return user;
    } catch (error) {
      throw new Error(getAuthErrorMessage(error.code));
    }
  }

  async function login(email, password) {
    try {
      checkSignInThrottle(email);
    } catch (error) {
      throw new Error(error.message);
    }

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Reset sign-in attempts on successful login
      signInAttempts.delete(email);

      // Fetch user data from Firestore
      const userDoc = await getDoc(doc(db, 'users', user.uid));

      if (!userDoc.exists()) {
        // Create user document if it doesn't exist
        const userData = {
          email: user.email,
          isAdmin: false,
          createdAt: new Date().toISOString(),
          uid: user.uid,
          lastPasswordChange: new Date().toISOString(),
        };
        await setDoc(doc(db, 'users', user.uid), userData);

        setCurrentUser({
          ...user,
          isAdmin: false,
        });
      } else {
        const userData = userDoc.data();
        setCurrentUser({
          ...user,
          isAdmin: userData.isAdmin,
        });
      }

      return user;
    } catch (error) {
      throw new Error(getAuthErrorMessage(error.code));
    }
  }

  async function logout() {
    try {
      // Clean up any existing subscriptions
      if (authUnsubscribe) {
        authUnsubscribe();
      }
      
      // Clear user state before signing out
      setCurrentUser(null);
      
      // Sign out from Firebase
      await signOut(auth);
      
      // Clear any local data
      localStorage.removeItem('lastLoginTime');
      sessionStorage.clear();
      
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear the local state even if Firebase logout fails
      setCurrentUser(null);
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Fetch user data from Firestore
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setCurrentUser({
              ...user,
              isAdmin: userData.isAdmin,
            });
          } else {
            // If no Firestore document exists, create one with isAdmin set to false
            const newUserData = {
              email: user.email,
              isAdmin: false,
              createdAt: new Date().toISOString(),
              uid: user.uid,
            };
            await setDoc(doc(db, 'users', user.uid), newUserData);
            setCurrentUser({
              ...user,
              isAdmin: false,
            });
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setCurrentUser(null);
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    setAuthUnsubscribe(() => unsubscribe);
    return () => unsubscribe();
  }, []);

  // Helper function to get user-friendly error messages
  function getAuthErrorMessage(errorCode) {
    switch (errorCode) {
      case 'auth/email-already-in-use':
        return 'This email is already registered. Please try logging in instead.';
      case 'auth/invalid-email':
        return 'Invalid email address. Please check and try again.';
      case 'auth/operation-not-allowed':
        return 'Email/password accounts are not enabled. Please contact support.';
      case 'auth/weak-password':
        return 'Password is too weak. Please use a stronger password that meets our requirements.';
      case 'auth/user-disabled':
        return 'This account has been disabled. Please contact support.';
      case 'auth/user-not-found':
        return 'Invalid email or password.'; // Generic message to prevent enumeration
      case 'auth/wrong-password':
        return 'Invalid email or password.'; // Generic message to prevent enumeration
      default:
        return 'An error occurred. Please try again.';
    }
  }

  const value = {
    currentUser,
    login,
    signup,
    logout,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
